import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/cartpage.scss";

const FREE_SHIPPING_THRESHOLD = siteConfig.shippingThreshold || 200;
const STANDARD_SHIPPING = siteConfig.shippingRates.standard;
const formatPrice = (value) =>
  `${siteConfig.currency.symbol}${Number(value).toFixed(2)} ${siteConfig.currency.code}`;

const CartPage = () => {
  const navigate = useNavigate();
  const { cartItems, removeFromCart, updateQuantity } = useContext(CartContext);
  const [orderNote, setOrderNote] = useState("");

  const lines = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = lines.reduce(
    (total, item) => total + Number(item.product.price) * item.quantity,
    0
  );
  const shipping = subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : STANDARD_SHIPPING;
  const amountUntilFree = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const progress = Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  if (lines.length === 0) {
    return (
      <main className="cart-empty">
        <p className="eyebrow">Your cart</p>
        <h1>Pack light. Start here.</h1>
        <p>
          Your cart is empty. Explore dependable layers and easy everyday gear
          made for the long way around.
        </p>
        <Link className="button button--primary" to="/shop" onClick={scrollToTop}>
          Continue shopping
        </Link>
        <div className="cart-empty__shipping">
          Free domestic shipping on orders over {formatPrice(FREE_SHIPPING_THRESHOLD)}
        </div>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <header className="cart-page__header">
        <p className="eyebrow">Your cart</p>
        <h1>Ready for the road.</h1>
        <Link to="/shop" onClick={scrollToTop}>
          Continue shopping ↗
        </Link>
      </header>

      <section className="cart-shipping-progress" aria-label="Free shipping progress">
        <p>
          {amountUntilFree > 0
            ? `You’re ${formatPrice(amountUntilFree)} away from free shipping.`
            : "Your order qualifies for free domestic shipping."}
        </p>
        <div>
          <span style={{ width: `${progress}%` }} />
        </div>
      </section>

      <div className="cart-page__layout">
        <section className="cart-lines" aria-label="Cart items">
          <div className="cart-lines__heading">
            <span>Product</span>
            <span>Quantity</span>
            <span>Total</span>
          </div>

          {lines.map((item) => {
            const { product } = item;
            const lineTotal = Number(product.price) * item.quantity;
            return (
              <article
                className="cart-line"
                key={`${item.id}-${item.size}-${item.color}`}
              >
                <Link
                  className="cart-line__image"
                  to={`/shopDetails/${product.id}`}
                  onClick={scrollToTop}
                >
                  <img src={product.image} alt={product.name} />
                </Link>
                <div className="cart-line__copy">
                  <Link to={`/shopDetails/${product.id}`} onClick={scrollToTop}>
                    {product.name}
                  </Link>
                  <p>{formatPrice(product.price)}</p>
                  <dl>
                    <div>
                      <dt>Color</dt>
                      <dd>{item.color}</dd>
                    </div>
                    <div>
                      <dt>Size</dt>
                      <dd>{item.size}</dd>
                    </div>
                  </dl>
                  <button
                    type="button"
                    onClick={() =>
                      removeFromCart(item.id, item.size, item.color)
                    }
                  >
                    Remove
                  </button>
                </div>
                <div className="cart-line__quantity" aria-label="Quantity">
                  <button
                    type="button"
                    aria-label={`Decrease ${product.name} quantity`}
                    onClick={() => {
                      if (item.quantity === 1) {
                        removeFromCart(item.id, item.size, item.color);
                      } else {
                        updateQuantity(
                          item.id,
                          item.size,
                          item.color,
                          item.quantity - 1
                        );
                      }
                    }}
                  >
                    −
                  </button>
                  <span>{item.quantity}</span>
                  <button
                    type="button"
                    aria-label={`Increase ${product.name} quantity`}
                    onClick={() =>
                      updateQuantity(
                        item.id,
                        item.size,
                        item.color,
                        item.quantity + 1
                      )
                    }
                  >
                    +
                  </button>
                </div>
                <strong className="cart-line__total">
                  {formatPrice(lineTotal)}
                </strong>
              </article>
            );
          })}

          <label className="cart-note">
            <span>Order note (optional)</span>
            <textarea
              value={orderNote}
              rows="4"
              maxLength="300"
              placeholder="Delivery notes, gift details, or anything we should know."
              onChange={(event) => setOrderNote(event.target.value)}
            />
            <small>{orderNote.length}/300</small>
          </label>
        </section>

        <aside className="cart-summary">
          <p className="eyebrow">Order summary</p>
          <div>
            <span>Subtotal</span>
            <strong>{formatPrice(subtotal)}</strong>
          </div>
          <div>
            <span>Estimated shipping</span>
            <strong>{shipping === 0 ? "Free" : formatPrice(shipping)}</strong>
          </div>
          <div className="cart-summary__total">
            <span>
              Estimated total
              <small>Taxes calculated at checkout</small>
            </span>
            <strong>{formatPrice(subtotal + shipping)}</strong>
          </div>
          <button
            type="button"
            onClick={() => {
              navigate("/checkout", { state: { orderNote } });
              scrollToTop();
            }}
          >
            Check out
          </button>
          <p className="cart-summary__secure">
            Secure demo checkout · No payment is processed
          </p>
        </aside>
      </div>
    </main>
  );
};

export default CartPage;
